import { useRef } from 'react';
import styled from 'styled-components';
import { Switch } from '@material-ui/core';
import toast from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import { Vehicle } from '../../../../../types/vehicle';
import { useIsAdmin } from '../../../../../hooks/selectors';
import { useIsTextOverflown } from '../../../../../hooks';
import { EditIcon } from '../../../../../components/Icons';
import Tooltip from '../../../../../components/Tooltip';
import { modalsToggle } from '../../../../../actions';
import { useSetDoNotRunMutation } from '../../../../../services/api/vehicle-do-not-run';
import { useVehicleListContext } from '../../context';
import { EditNoteModal } from './EditNoteModal';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 120px;
  padding-right: 16px;
  border-right: 1px solid #00000033;
`;

const Label = styled.div`
  color: #757575;
  font-size: 12px;
  font-weight: 400;
`;

const NoteText = styled.span<{ maxWidth?: number }>`
  font-weight: 500;
  font-size: 12px;
  color: #000;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : 'none')};
`;

const NotesPlaceholder = styled.div`
  color: #b0b0b0;
  font-weight: 500;
  font-size: 12px;
`;

const NoteRow = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
`;

const EditRow = styled.div`
  display: flex;
  gap: 2px;
  align-items: center;
  font-size: 12px;
  cursor: pointer;
`;

const EditLabel = styled.div`
  text-decoration: underline;
  color: #3994de;
  margin-left: 4px;

  &:hover {
    color: #000;
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ToggleLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: #000;
`;

type Props = { vehicle: Vehicle };

export const DoNotRunControl = ({ vehicle }: Props) => {
  const isSuperAdmin = useIsAdmin();
  const dispatch = useDispatch();
  const { localUpdate } = useVehicleListContext();
  const [setDoNotRun] = useSetDoNotRunMutation();

  const ref = useRef<HTMLSpanElement>(null);
  const isTextOverflown = useIsTextOverflown(ref);

  if (!isSuperAdmin) return null;

  const openModal = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(modalsToggle(`doNotRunNoteModal-${vehicle.id}`));
  };

  const handleToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    try {
      await setDoNotRun({ vehicleId: vehicle.id, doNotRun: next }).unwrap();
      localUpdate(vehicle.id, { do_not_run: next });
      toast.success(next ? 'Marked Do Not Run.' : 'Removed Do Not Run.');
    } catch (err) {
      toast.error('Could not update Do Not Run.');
    }
  };

  return (
    <Container onClick={e => e.stopPropagation()}>
      <NoteRow>
        <Label>Notes:</Label>
        {vehicle.do_not_run_note ? (
          <Tooltip isTextOverflown={isTextOverflown} disableFocusListener title={vehicle.do_not_run_note} arrow light>
            <NoteText maxWidth={90} ref={ref}>
              {vehicle.do_not_run_note}
            </NoteText>
          </Tooltip>
        ) : (
          <NotesPlaceholder>No Notes</NotesPlaceholder>
        )}
      </NoteRow>

      <EditRow onClick={openModal}>
        <EditIcon />
        <EditLabel>Add Notes</EditLabel>
      </EditRow>

      <ToggleRow>
        <Switch
          color="primary"
          checked={!!vehicle.do_not_run}
          onChange={handleToggle}
          onClick={e => e.stopPropagation()}
        />
        <ToggleLabel>Do Not Run</ToggleLabel>
      </ToggleRow>

      <EditNoteModal vehicle={vehicle} />
    </Container>
  );
};

export default DoNotRunControl;
